/**
 * Deterministic rotation helpers for internal linking.
 *
 * Sibling lists are capped so pages stay light, which means the cap decides who
 * receives inbound links. Slicing the same first N entries on every page starves
 * everything past the cap (alphabetically late models, extra models of a brand)
 * of internal links. Rotating the slice by a hash of the current path keeps the
 * output stable per URL — important for caching and diffs — while spreading
 * inbound links evenly across the whole set.
 */

/** Stable 32-bit FNV-1a hash; same input always yields the same offset. */
export function stableHash(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * A window of `size` items starting just after `currentKey`'s position.
 *
 * When the current item isn't in the list (the caller already filtered it out),
 * the offset falls back to a hash of the key instead of index 0 — the bug that
 * left every page rendering the identical first N siblings.
 */
export function rotatedWindow<T>(
  items: T[],
  size: number,
  currentKey: string,
  keyOf: (item: T) => string,
): T[] {
  if (items.length === 0) return [];
  if (items.length <= size) return items;
  const found = items.findIndex((i) => keyOf(i) === currentKey);
  const anchor = found >= 0 ? found : stableHash(currentKey) % items.length;
  return Array.from({ length: size }, (_, i) => items[(anchor + 1 + i) % items.length]!);
}

/** One model per brand, rotated per page so brands with several models spread their links. */
export function pickCrossMakeSiblings(
  groups: { makeName: string; makeSlug: string; models: { name: string; slug: string }[] }[],
  seed: string,
  limit: number,
): { name: string; slug: string; makeName: string; makeSlug: string }[] {
  const picks = groups
    .filter((g) => g.models.length > 0)
    .map((g) => {
      const m = g.models[stableHash(`${seed}:${g.makeSlug}`) % g.models.length]!;
      return { name: m.name, slug: m.slug, makeName: g.makeName, makeSlug: g.makeSlug };
    });
  return rotatedWindow(picks, limit, seed, (p) => p.makeSlug);
}
